<script lang="ts">
  import type { ImportedCity, InventoryItem, Plan, ResolvedBuilding } from '$lib/types'
  import { activeTool, type ActiveTool } from '$lib/editor'
  import InventoryTab from './InventoryTab.svelte'
  import PaletteTab from './PaletteTab.svelte'

  // Tool cluster: rendered inside the panel rather than the top toolbar so
  // it sits next to the inventory/palette the user is reaching for. The
  // styling is panel-aware (surface-card-recessed default, accent-tinted
  // active) — ToolButton's chrome variant doesn't read on the bone-white
  // card surface in the light theme.
  interface ToolEntry {
    id: ActiveTool
    ico: string
    label: string
    title: string
  }
  const tools: readonly ToolEntry[] = [
    { id: 'select', ico: '↖', label: 'Select', title: 'Select tool (Esc)' },
    { id: 'road', ico: '═', label: 'Road', title: 'Road tool (hold R)' },
    { id: 'store', ico: '⊟', label: 'Store', title: 'Store tool (hold S)' },
  ]

  interface Props {
    plan: Plan
    importedCity: ImportedCity | null
    onInventoryPickup: (item: InventoryItem, resolved: ResolvedBuilding | null) => void
    onInventoryDiscard: (items: InventoryItem[], label: string, action: 'one' | 'all') => void
    onPalettePickup: (resolved: ResolvedBuilding) => void
    /** Send every placed building to inventory in one command. */
    onClearAllBuildings: () => void
  }

  const {
    plan,
    importedCity,
    onInventoryPickup,
    onInventoryDiscard,
    onPalettePickup,
    onClearAllBuildings,
  }: Props = $props()

  type TabKey = 'inventory' | 'palette'
  let activeTab: TabKey = $state('inventory')
</script>

<aside
  class="absolute right-0 top-0 z-10 flex h-full w-80 flex-col border-l border-border-rule bg-surface-card/95 text-text-primary shadow-2xl"
>
  <div class="flex border-b border-border-rule" role="tablist">
    {#each [{ key: 'inventory', label: 'Inventory' }, { key: 'palette', label: 'Palette' }] as tab (tab.key)}
      <button
        type="button"
        role="tab"
        aria-selected={activeTab === tab.key}
        class="flex-1 px-3 py-2 text-xs font-semibold uppercase tracking-wide transition {activeTab ===
        tab.key
          ? 'border-b-2 border-accent text-accent'
          : 'text-text-muted hover:text-text-primary'}"
        onclick={() => (activeTab = tab.key as TabKey)}>{tab.label}</button
      >
    {/each}
  </div>

  <div class="flex-1 overflow-y-auto">
    {#if activeTab === 'inventory'}
      <InventoryTab
        inventory={plan.inventory}
        {importedCity}
        onPickup={onInventoryPickup}
        onDiscard={onInventoryDiscard}
      />
    {:else}
      <PaletteTab {importedCity} onPickup={onPalettePickup} />
    {/if}
  </div>

  <!-- Tools sit at the bottom in their own section, separated from the
       buildings area above by a 2px divider. shrink-0 keeps the section
       at its natural height when the inventory/palette content tries to
       expand into it. -->
  <section
    class="shrink-0 border-t-2 border-border-rule bg-surface-card px-4 py-3"
    aria-label="Tools"
  >
    <h3 class="text-xs font-semibold uppercase tracking-wide text-text-secondary">Tool</h3>
    <div class="mt-2 flex gap-1" role="group" aria-label="Tool mode">
      {#each tools as tool (tool.id)}
        <button
          type="button"
          class="flex-1 rounded border px-2 py-1 text-xs transition {$activeTool === tool.id
            ? 'border-accent bg-accent text-white'
            : 'border-transparent bg-surface-card-recessed text-text-primary hover:bg-text-primary/10'}"
          title={tool.title}
          aria-pressed={$activeTool === tool.id}
          onclick={() => activeTool.set(tool.id)}
        >
          <span aria-hidden="true">{tool.ico}</span>
          <span class="ml-1">{tool.label}</span>
        </button>
      {/each}
    </div>

    <button
      type="button"
      class="mt-3 w-full rounded border border-status-neg/60 bg-status-neg/30 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:border-status-neg hover:bg-status-neg/55 disabled:cursor-not-allowed disabled:opacity-40"
      disabled={plan.buildings.length === 0}
      onclick={onClearAllBuildings}
      title="Send every placed building to inventory (undo with Ctrl+Z)"
    >
      Clear all buildings
    </button>
  </section>
</aside>
